import React, { useState, useEffect } from 'react';
import {
  BuildingOfficeIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useUI } from '../../contexts/UIContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { apiRequest } from '../../utils/apiUtils';

const AdminOrganizationSettings = () => {
  const { showNotification } = useUI();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  
  const [settings, setSettings] = useState({
    organization_name: '',
    organization_description: '',
    organization_timezone: 'UTC',
    support_email: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    if (isLoadingSettings) {
      return;
    }

    try {
      setLoading(true);
      setIsLoadingSettings(true);
      const response = await apiRequest('/admin/system-settings', { skipThrottle: true });
      if (response?.data) {
        setSettings(prev => ({
          ...prev,
          organization_name: response.data.organization?.name || '',
          organization_description: response.data.organization?.description || '',
          organization_timezone: response.data.organization?.timezone || 'UTC',
          support_email: response.data.organization?.support_email || ''
        }));
      }
    } catch (error) {
      console.error('Error loading organization settings:', error);
      showNotification('Failed to load organization settings', 'error');
    } finally {
      setLoading(false);
      setIsLoadingSettings(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Create payload with organization settings and keep defaults for other sections
      const payload = {
        organization: {
          name: settings.organization_name,
          description: settings.organization_description,
          timezone: settings.organization_timezone,
          support_email: settings.support_email,
          // Keep existing defaults
          logo_url: '',
          website: '',
          primary_color: '#3B82F6'
        },
        // Keep existing defaults for other sections
        security: {
          password_min_length: 8,
          password_require_uppercase: true,
          password_require_lowercase: true,
          password_require_numbers: true,
          password_require_symbols: false,
          session_timeout_minutes: 480,
          max_login_attempts: 5,
          lockout_duration_minutes: 15,
          two_factor_required: false,
          allowed_domains: []
        },
        data_retention: {
          validation_results_days: 365,
          lineage_data_days: 730,
          user_activity_days: 90,
          system_logs_days: 30,
          automated_cleanup_enabled: true,
          cleanup_schedule: '2'
        },
        system_limits: {
          max_users: 50,
          max_connections: 20,
          max_concurrent_jobs: 5,
          max_data_objects_per_connection: 10000,
          api_rate_limit_per_minute: 1000,
          max_file_upload_mb: 100
        },
        maintenance: {
          backup_enabled: true,
          backup_schedule: '1',
          backup_retention_days: 30,
          maintenance_window_start: '02:00',
          maintenance_window_end: '04:00',
          maintenance_timezone: 'UTC',
          auto_updates_enabled: false
        }
      };
      
      await apiRequest('/admin/system-settings', {
        method: 'POST',
        data: payload
      });
      showNotification('Organization settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving organization settings:', error);
      showNotification('Failed to save organization settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Organization Settings</h3>
          <p className="mt-1 text-sm text-gray-500">
            Manage your organization's basic information and settings.
          </p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {saving ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Organization Settings */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-6">
          <BuildingOfficeIcon className="h-5 w-5 text-primary-600 mr-2" />
          <h4 className="text-base font-medium text-gray-900">Organization Information</h4>
        </div>
        
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Organization Name</label>
              <input
                type="text"
                value={settings.organization_name}
                onChange={(e) => handleInputChange('organization_name', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                placeholder="Your Company Name"
              />
              <p className="mt-1 text-xs text-gray-500">This appears in emails and throughout the application</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Timezone</label>
              <select
                value={settings.organization_timezone}
                onChange={(e) => handleInputChange('organization_timezone', e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Europe/Paris">Paris (CET/CEST)</option>
                <option value="Europe/Berlin">Berlin (CET/CEST)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Asia/Shanghai">Shanghai (CST)</option>
                <option value="Asia/Singapore">Singapore (SGT)</option>
                <option value="Australia/Sydney">Sydney (AEDT/AEST)</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">Default timezone for reports and schedules</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              value={settings.organization_description}
              onChange={(e) => handleInputChange('organization_description', e.target.value)}
              rows={3}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="Brief description of your organization (optional)"
            />
            <p className="mt-1 text-xs text-gray-500">Internal description for your team</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Support Email</label>
            <input
              type="email"
              value={settings.support_email}
              onChange={(e) => handleInputChange('support_email', e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              placeholder="support@yourcompany.com"
            />
            <p className="mt-1 text-xs text-gray-500">Contact email for support and notifications</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrganizationSettings;